import React, { useState, useEffect } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import type { Project, Settings } from '../../types';
import ShareButtons from '../../components/ShareButtons';
import ProjectCard from '../../components/ProjectCard';
import { useDocumentBySlug } from '../../hooks/useDocumentBySlug';
import { useCollection } from '../../hooks/useCollection';
import OptimizedImage from '../../components/OptimizedImage';
import { useProjectEditor } from './PublicLayout';
import { useSEO } from '../../hooks/useSEO';

interface ProjectDetailPageProps {
    settings: Settings;
}

const EditIcon: React.FC<{className?: string}> = ({className}) => (<svg className={className} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path></svg>);

const getStatusColor = (status: Project['status']) => {
    switch (status) {
        case 'Completed': return 'bg-green-100 text-green-800';
        case 'Under Construction': return 'bg-yellow-100 text-yellow-800';
        case 'Concept': return 'bg-blue-100 text-blue-800';
        case 'Proposal': return 'bg-purple-100 text-purple-800';
        default: return 'bg-gray-100 text-gray-800';
    }
}

const ProjectInfo: React.FC<{project: Project}> = ({project}) => (
    <div>
         <h3 className="text-xl font-bold mb-4 font-heading">Information</h3>
         <div className="text-sm space-y-3 mb-6 border-l-2 border-gray-200 pl-4">
            {project.client && <p><strong className="font-semibold block text-gray-800">Client:</strong> {project.client}</p>}
            {project.location && <p><strong className="font-semibold block text-gray-800">Location:</strong> {project.location}</p>}
            {project.price && project.price > 0 && <p><strong className="font-semibold block text-gray-800">Project Price:</strong> RM {project.price.toLocaleString()}</p>}
            {project.status && <p><strong className="font-semibold block text-gray-800">Status:</strong>
                <span className={`ml-2 inline-block px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(project.status)}`}>
                    {project.status}
                </span>
            </p>}
        </div>
        <ShareButtons title={project.title} />
    </div>
);

const ProjectTeam: React.FC<{project: Project}> = ({project}) => {
    if (!project.projectTeam || project.projectTeam.length === 0) return null;

    return (
        <div className="mt-12">
            <h3 className="text-xl font-bold mb-4 font-heading">Project Team & Consultants</h3>
            <div className="space-y-4">
                {project.projectTeam.map((member, index) => (
                    <div key={index} className="flex items-center gap-4">
                        <img 
                            src={member.avatarUrl || `https://ui-avatars.com/api/?name=${member.name.replace(/ /g, '+')}&background=e5e7eb&color=4b5563`}
                            alt={member.name}
                            className="w-12 h-12 rounded-full object-cover bg-gray-200 flex-shrink-0"
                        />
                        <div>
                            {member.name && <p className="font-semibold text-gray-800">{member.name}</p>}
                            {member.role && <p className="text-sm text-gray-600">{member.role}</p>}
                            {member.company && <p className="text-sm text-gray-500 italic">{member.company}</p>}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    )
}

const ClassicLayout: React.FC<{project: Project}> = ({project}) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    
    return (
         <div className="pt-24">
            {/* Image Gallery Section */}
            <div className="mb-12">
                <div className="w-full aspect-video bg-gray-100 flex items-center justify-center">
                     <OptimizedImage 
                        src={project.images[currentIndex]?.url}
                        size={1920}
                        fallbackSrc="https://picsum.photos/1200/800"
                        alt={`${project.title} - view ${currentIndex + 1}`} 
                        className="w-full h-full object-cover"
                    />
                </div>
                {project.images.length > 1 && (
                     <div className="bg-brand-dark/90 backdrop-blur-sm py-2">
                        <div className="container mx-auto px-6 flex justify-center gap-2 flex-wrap">
                            {project.images.map((image, index) => (
                                <button key={index} onClick={() => setCurrentIndex(index)} className={`w-24 h-16 block transition-all ${currentIndex === index ? 'ring-2 ring-offset-2 ring-offset-brand-dark ring-white' : 'opacity-60 hover:opacity-100'}`}>
                                    <OptimizedImage src={image.url} size={200} alt={`thumbnail ${index + 1}`} className="w-full h-full object-cover"/>
                                </button>
                            ))}
                         </div>
                     </div>
                )}
            </div>

            <div className="container mx-auto px-6">
                <div className="max-w-4xl mx-auto mb-12 text-center">
                    <p className="text-sm uppercase tracking-widest text-gray-500 font-header">{project.category} / {project.year}</p>
                    <h1 className="text-3xl sm:text-4xl md:text-6xl font-extrabold my-4 tracking-tight font-heading">{project.title}</h1>
                    <p className="text-lg text-gray-600 font-body">{project.description}</p>
                </div>

                <div className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-12">
                    <div className="md:col-span-2">
                        <h2 className="text-2xl font-bold mb-4 border-b pb-2 font-heading">Project Details</h2>
                        <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed font-body" dangerouslySetInnerHTML={{ __html: project.details }}></div>
                    </div>
                    <div>
                        <ProjectInfo project={project} />
                        <ProjectTeam project={project} />
                    </div>
                </div>
            </div>
        </div>
    );
};

const SplitLayout: React.FC<{project: Project}> = ({project}) => (
    <div className="container mx-auto px-6 pt-32">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
             <div className="lg:sticky lg:top-32 self-start">
                 <div className="space-y-2">
                    {project.images.map((image, index) => (
                        <OptimizedImage key={index} src={image.url} size={1200} alt={`${project.title} ${index+1}`} className="w-full h-auto object-cover" />
                    ))}
                 </div>
            </div>
            <div className="lg:pt-0">
                <p className="text-sm uppercase tracking-widest text-gray-500 font-header">{project.category} / {project.year}</p>
                <h1 className="text-3xl sm:text-4xl md:text-6xl font-extrabold my-4 tracking-tight font-heading">{project.title}</h1>
                <p className="text-lg text-gray-600 mb-8 font-body">{project.description}</p>
                <div className="mb-12"><ProjectInfo project={project} /></div>
                <h2 className="text-2xl font-bold mb-4 border-b pb-2 font-heading">Project Details</h2>
                <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed font-body" dangerouslySetInnerHTML={{ __html: project.details }}></div>
                <ProjectTeam project={project} />
            </div>
        </div>
    </div>
);

const ImmersiveLayout: React.FC<{project: Project}> = ({project}) => {
    return (
        <div>
            <div className="h-[80vh] w-full relative flex items-center justify-center text-center text-white pt-24">
                 <OptimizedImage src={project.images[0].url} size={1920} alt={project.title} className="absolute inset-0 w-full h-full object-cover" />
                 <div className="absolute inset-0 bg-black bg-opacity-50"></div>
                 <div className="relative z-10 p-8">
                    <p className="text-sm uppercase tracking-widest font-header">{project.category} / {project.year}</p>
                    <h1 className="text-3xl sm:text-4xl md:text-7xl font-extrabold my-4 tracking-tight font-heading">{project.title}</h1>
                 </div>
            </div>
            <div className="container mx-auto px-6 py-16">
                 <div className="max-w-3xl mx-auto">
                    <p className="text-xl text-gray-600 leading-relaxed text-center mb-16 font-body">{project.description}</p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mb-16">
                        <div className="md:col-span-2 prose prose-lg max-w-none" dangerouslySetInnerHTML={{ __html: project.details.split('</p>')[0] + '</p>' }}>
                        </div>
                        <div className="self-start">
                            <ProjectInfo project={project}/>
                            <ProjectTeam project={project} />
                        </div>
                    </div>

                    {project.images.slice(1).map((image, index) => (
                         <OptimizedImage key={index} src={image.url} size={1200} alt={`${project.title} ${index+2}`} className="w-full h-auto my-16"/>
                    ))}

                 </div>
            </div>
        </div>
    );
};

const ProjectDetailPage: React.FC<ProjectDetailPageProps> = ({ settings }) => {
    const { slug } = useParams<{ slug: string }>();
    const { document: project, isLoading } = useDocumentBySlug<Project>('projects', slug);
    const { data: allProjects } = useCollection<Project>('projects');
    const [isDevMode, setIsDevMode] = useState(false);
    const editorContext = useProjectEditor();

    useEffect(() => {
        setIsDevMode(sessionStorage.getItem('devMode') === 'true');
    }, []);

    useSEO({
        title: project ? `${project.title} | ${settings.companyName}` : undefined,
        description: project?.description,
        ogImage: project?.images?.[0]?.url
    }, [project, settings]);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [slug]);

    if (isLoading) {
        return <div className="h-screen flex items-center justify-center"><p>Loading project...</p></div>;
    }

    if (!project) {
        return <Navigate to="/" />;
    }

    const otherProjects = (allProjects || [])
        .filter(p => p.id !== project.id && p.published)
        .slice(0, 3);
        
    const renderLayout = () => {
        switch (settings.detailLayout) {
            case 'split':
                return <SplitLayout project={project} />;
            case 'immersive':
                return <ImmersiveLayout project={project} />;
            case 'classic':
            default:
                return <ClassicLayout project={project} />;
        }
    }


    return (
        <div className="bg-brand-light text-brand-dark pb-16">
            {isDevMode && (
                <button
                    onClick={() => editorContext?.openProjectEditor(project)}
                    className="fixed bottom-24 right-5 z-50 px-4 py-3 bg-gray-800 text-white rounded-full shadow-lg hover:bg-gray-700 transition-colors flex items-center gap-2"
                    title="Edit Project"
                >
                    <EditIcon className="w-5 h-5" />
                    <span className="text-sm font-semibold">Edit Project</span>
                </button>
            )}
            {renderLayout()}
            
            {/* Other Projects Section */}
            {otherProjects.length > 0 && (
                 <div className="max-w-6xl mx-auto mt-24">
                    <div className="container mx-auto px-6">
                        <hr className="border-gray-200 mb-16" />
                        <h2 className="text-3xl font-bold text-center mb-12 font-heading">Other Projects</h2>
                         <div className="grid grid-cols-1 md:grid-cols-3 gap-1">
                            {otherProjects.map(p => (
                                <ProjectCard key={p.id} project={p} />
                            ))}
                        </div>
                    </div>
                 </div>
            )}
             <style>{`
                .prose p { margin-bottom: 1.25em; }
                .prose h1, .prose h2, .prose h3, .prose h4, .prose h5, .prose h6 { font-family: var(--font-family-heading); font-weight: 700; margin-top: 2em; margin-bottom: 1em; }
                .prose ul, .prose ol { margin-left: 1.5em; }
                .prose a { color: inherit; }
                .prose img { margin-top: 2em; margin-bottom: 2em; }
            `}</style>
        </div>
    );
};

export default ProjectDetailPage;